import { API_URL } from '@/network/http';

export type HealthStatus = 'healthy' | 'backend_down' | 'database_down' | 'network_error';

interface HealthCheckResult {
  status: HealthStatus;
  message?: string;
}

class HealthCheckService {
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private listeners: Set<(status: HealthStatus) => void> = new Set();
  private lastStatus: HealthStatus = 'healthy';

  /**
   * Derive backend base URL (without /api/v1) for health endpoints
   */
  private getBaseUrl(): string {
    return API_URL.replace(/\/api\/v1\/?$/, '');
  }

  async checkHealth(): Promise<HealthCheckResult> {
    try {
      if (!navigator.onLine) {
        // navigator.onLine can be unreliable, but if it says offline we still try
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const baseUrl = this.getBaseUrl();

      try {
        const response = await fetch(`${baseUrl}/health`, {
          signal: controller.signal,
          headers: { 'Cache-Control': 'no-cache' },
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          return { status: 'backend_down', message: 'Backend returned error' };
        }

        // Backend is up — check database
        try {
          const dbResponse = await fetch(`${baseUrl}/health/db`, {
            headers: { 'Cache-Control': 'no-cache' },
          });
          if (dbResponse.status === 503) {
            return { status: 'database_down', message: 'Database unavailable' };
          }
          return { status: 'healthy', message: 'All systems operational' };
        } catch {
          // DB endpoint failed but basic health passed — assume healthy
          return { status: 'healthy', message: 'Backend operational' };
        }
      } catch (fetchError: unknown) {
        clearTimeout(timeoutId);
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          return { status: 'backend_down', message: 'Backend timeout' };
        }
        return { status: 'backend_down', message: 'Cannot reach backend' };
      }
    } catch {
      return { status: 'network_error', message: 'Network error' };
    }
  }

  startMonitoring(interval: number = 60000) {
    if (this.checkInterval) {
      this.stopMonitoring();
    }

    this.performCheck();
    this.checkInterval = setInterval(() => this.performCheck(), interval);

    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  stopMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  subscribe(callback: (status: HealthStatus) => void) {
    this.listeners.add(callback);
    callback(this.lastStatus);
    return () => {
      this.listeners.delete(callback);
    };
  }

  getLastStatus(): HealthStatus {
    return this.lastStatus;
  }

  private async performCheck() {
    const result = await this.checkHealth();
    if (result.status !== this.lastStatus) {
      this.lastStatus = result.status;
      this.notifyListeners(result.status);
    }
  }

  private notifyListeners(status: HealthStatus) {
    this.listeners.forEach((callback) => callback(status));
  }

  private handleOnline = () => {
    this.performCheck();
  };

  private handleOffline = () => {
    this.lastStatus = 'network_error';
    this.notifyListeners('network_error');
  };
}

export const healthCheckService = new HealthCheckService();
